/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
    createContext, useContext, ReactNode,
    useMemo,
    useEffect,
} from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useManagerProfile } from 'prodivers/managerProfile';
import { getClientId } from 'utils';
import { useContactGroupOwners } from 'hooks/helpers/useContactGroupOwners';
import { accessPolicies } from 'prodivers/managerProfile/constants';
import { useProductsList } from 'hooks/useProducts';
import { PRODUCT_VALUES_FOR_3B, PRODUCTS } from 'constants/constants';
import arrayContains from 'utils/arrayUtils';
import { actionTypes, CreateProposalReducer } from './CreateProposalReducer';
import { CreateProposalContextProps, CreateProposalState } from './CreateProposalState.types';
import { getMainClientData } from '../helpers';

export const createNewProposalStorageKey = 'createNewProposal';

const defaultState: CreateProposalState = {
    currentPortfolioSelector: null,
    mainOwner: null,
    jaClient: null,
    selectedProductId: null,
    connectionGroupId: undefined,
    currentClientSelector: null,
    selectedProductTypeSelector: null,
    is3bProductGroupSelector: false,
    selectedProductGroupIdSelector: null,
    isLoadingProducts: false,
    products: null,
    groupedProducts: null,
    errorProducts: null,
    isKuDi: false,
    selectedProductSelector: null,
    selectedProposalOwner: null,
    agentInformation: null,
    selectedLanguage: { id: 'de', label: 'DE' },
};

const CreateProposalContext = createContext<CreateProposalContextProps | undefined>(undefined);

export const CreateProposalProvider = ({ children }: { children: ReactNode }) => {
    const getInitialState = (): CreateProposalState => {
        const stored = sessionStorage.getItem(createNewProposalStorageKey);

        if (stored) {
            try {
                const parsed = JSON.parse(stored);

                return { ...defaultState, ...parsed };
            } catch {
                return defaultState;
            }
        }

        return defaultState;
    };

    const [state, dispatch] = React.useReducer(CreateProposalReducer, getInitialState());
    const { hasAccessPolicy } = useManagerProfile();
    const { dfsClientId } = useParams();
    const clientId = getClientId(dfsClientId);

    const {
        isLoadingProducts,
        groupedProducts,
        products,
        errorProducts,
    } = useProductsList(clientId, true, false);

    const { mainOwner, jaClient } = useContactGroupOwners({ connectionGroupId: (state?.connectionGroupId as number || undefined), clientId });

    const isPA = useMemo(() => mainOwner?.age && (+mainOwner.age < 18), [mainOwner?.age]);
    // Need to change
    const isKuDi = useMemo(() => hasAccessPolicy(accessPolicies?.EnableBeneficiaryChange), [hasAccessPolicy]);

    const currentClientData = useMemo(() => mainOwner?.clientData, [mainOwner?.clientData]);
    const jointClientData = useMemo(() => jaClient?.clientData, [jaClient?.clientData]);

    const currentClientSelector = useMemo(() => (getMainClientData(currentClientData)), [currentClientData]);
    const jointClientSelector = useMemo(() => (getMainClientData(jointClientData)), [jointClientData]);

    const selectedProductGroupSelector = useMemo(
        () => groupedProducts?.find(({ id }) => ((state?.selectedProductId === id) || (id === 999999 && PRODUCT_VALUES_FOR_3B?.includes(state?.selectedProductId as number)))),
        [state?.selectedProductId, groupedProducts],
    );

    const selectedProductTypeSelector = useMemo(() => (selectedProductGroupSelector?.settings?.productKey?.toLowerCase()), [selectedProductGroupSelector?.settings?.productKey]);
    const selectedProductSelector = useMemo(() => products?.find(({ id }) => (state?.selectedProductId === id)), [state?.selectedProductId, products]);

    const is3bProductGroupSelector = useMemo(() => (arrayContains([selectedProductTypeSelector, selectedProductSelector?.settings?.productKey], PRODUCTS.bbb)), [selectedProductTypeSelector, selectedProductSelector]);
    const selectedProductGroupIdSelector = useMemo(() => (selectedProductGroupSelector?.id ?? -1), [selectedProductGroupSelector?.id]);

    const setSelectedProductIdAction = (productId: string | number | null) => {
        dispatch({ type: actionTypes.SET_SELECTED_PRODUCT_ID, payload: productId });
    };

    const saveProposalData = (value: Partial<CreateProposalState>) => {
        dispatch({ type: actionTypes.SAVE_DATA, payload: value });
    };

    const setConnectionGroupIdAction = (connectionGroupId: string) => {
        dispatch({ type: actionTypes.SET_CONNECTION_GROUP_ID, payload: connectionGroupId });
    };
    const setSelectedProductOfferId = (selectedProductOfferId: string | number | null) => {
        dispatch({ type: actionTypes.SET_SELECTED_PRODUCT_ID, payload: selectedProductOfferId });
    };
    const setSelectedProposalOwnerAction = (selectedProposalOwner: any) => {
        dispatch({ type: actionTypes.SET_SELECTED_PROPOSAL_OWNER, payload: selectedProposalOwner });
    };
    const setAgentInformationAction = (agentInformation: any) => {
        dispatch({ type: actionTypes.SET_AGENT_INFORMATION, payload: agentInformation });
    };
    const setSelectedLanguageAction = (selectedLanguage: any) => {
        dispatch({ type: actionTypes.SET_SELECTED_LANGUAGE, payload: selectedLanguage });
    };

    useEffect(() => () => {
        sessionStorage.removeItem(createNewProposalStorageKey);
    }, []);

    const cachedState = useMemo(() => ({
        ...state,
        isKuDi,
        mainOwner,
        jaClient,
        currentClientSelector,
        jointClientSelector,
        selectedProductTypeSelector,
        is3bProductGroupSelector,
        selectedProductGroupIdSelector,
        isLoadingProducts,
        products,
        groupedProducts,
        errorProducts,
        selectedProductSelector,
        selectedProductGroupSelector,
        setSelectedProductIdAction,
        setConnectionGroupIdAction,
        saveProposalData,
        setSelectedProductOfferId,
        setSelectedProposalOwnerAction,
        setAgentInformationAction,
        setSelectedLanguageAction,
    }), [
        state,
        isKuDi,
        isPA,
        mainOwner,
        jaClient,
        currentClientSelector,
        jointClientSelector,
        selectedProductTypeSelector,
        is3bProductGroupSelector,
        selectedProductGroupIdSelector,
        isLoadingProducts,
        products,
        groupedProducts,
        errorProducts,
        selectedProductSelector,
        selectedProductGroupSelector,
        setSelectedProductIdAction,
        setConnectionGroupIdAction,
        saveProposalData,
        setSelectedProductOfferId,
        setSelectedProposalOwnerAction,
        setAgentInformationAction,
        setSelectedLanguageAction,
    ]);

    return (
        <CreateProposalContext.Provider value={cachedState}>
            {children}
        </CreateProposalContext.Provider>
    );
};

export const useCreateProposal = () => {
    const context = useContext(CreateProposalContext);
    const t = useTranslation();

    React.useEffect(() => {
        if (context) {
            sessionStorage.setItem(createNewProposalStorageKey, JSON.stringify({ ...context }));
        }
    }, [context, t]);

    if (!context) {
        throw new Error('useCreateProposal must be used within a CreateProposalProvider');
    }

    return context;
};
